import { h, clear } from './dom.js';
import { FX_LIBRARY } from '../core/fx/library.js';

// Mixer panel: one strip per channel. Volume fader, pan knob (slider for now),
// mute/solo toggles. Future modules can register FX channels and they'll show
// up here automatically.

export function mountMixerPanel(ctx, slot) {
    clear(slot);

    // Spectrum analyzer (FFT) lives in the mixer header. Updates per frame
    // alongside the VU meters in tickMeters().
    const spectrumCanvas = h('canvas.spectrum', { width: 480, height: 56 });
    const header = h('div.lane-header', {}, [
        h('h2', {}, 'Mixer'),
        h('div.spectrum-wrap', {}, [spectrumCanvas]),
    ]);
    const strips = h('div.mixer-strips');
    slot.appendChild(header);
    slot.appendChild(strips);

    // One rAF loop drives all VU meters in the panel. Each .meter-fill DOM
    // node is tagged with its channel id (or 'master') and its height is
    // updated every frame from the live mixer meters.
    const meterRefs = new Map(); // channelId → fill element
    let meterRafId = null;
    const dbToHeight = (db) => {
        if (!isFinite(db)) return 0;
        // Map -60..0 dB → 0..100%, clamping at the ends.
        const pct = (db + 60) / 60;
        return Math.max(0, Math.min(1, pct)) * 100;
    };
    const ctx2d = spectrumCanvas.getContext('2d');
    function drawSpectrum() {
        const data = ctx.mixer.getMasterSpectrum?.();
        const W = spectrumCanvas.width;
        const H = spectrumCanvas.height;
        ctx2d.clearRect(0, 0, W, H);
        if (!data) return;
        // Tone.FFT returns dB values; map -100..0 → 0..H. We log-scale the
        // bin index so low frequencies aren't crushed against the left edge.
        const bins = data.length;
        const bars = 64;
        for (let i = 0; i < bars; i++) {
            const t0 = i / bars;
            const t1 = (i + 1) / bars;
            const i0 = Math.floor(Math.pow(t0, 2) * bins);
            const i1 = Math.max(i0 + 1, Math.floor(Math.pow(t1, 2) * bins));
            let max = -Infinity;
            for (let j = i0; j < i1 && j < bins; j++) if (data[j] > max) max = data[j];
            const norm = Math.max(0, Math.min(1, (max + 100) / 100));
            const barH = norm * H;
            const x = (i / bars) * W;
            const w = (W / bars) * 0.85;
            ctx2d.fillStyle = max > -10 ? '#ef596f' : max > -30 ? '#f4d35e' : '#4ec9b0';
            ctx2d.fillRect(x, H - barH, w, barH);
        }
    }

    function tickMeters() {
        for (const [id, fill] of meterRefs) {
            const db = id === 'master' ? ctx.mixer.getMasterLevel() : ctx.mixer.get(id)?.getLevel?.();
            const pct = dbToHeight(db ?? -Infinity);
            fill.style.height = `${pct}%`;
            fill.style.background = db > -3 ? '#e06c75' : db > -12 ? '#f4d35e' : '#4ec9b0';
        }
        drawSpectrum();
        meterRafId = requestAnimationFrame(tickMeters);
    }
    tickMeters();

    function render() {
        clear(strips);
        meterRefs.clear();
        // Master strip is always first.
        strips.appendChild(masterStrip());
        for (const ch of ctx.mixer.all()) strips.appendChild(channelStrip(ch));
    }

    function masterStrip() {
        const master = ctx.mixer.getMaster();
        const volEl = h('input.fader', {
            type: 'range', min: -40, max: 6, step: 0.5,
            value: master.volume.value,
            orient: 'vertical',
            onInput: (e) => { master.volume.value = parseFloat(e.target.value); volReadout.textContent = fmtDb(master.volume.value); },
        });
        const volReadout = h('span.readout', {}, fmtDb(master.volume.value));
        const meterFill = h('div.meter-fill');
        meterRefs.set('master', meterFill);
        return h('div.strip.master-strip', {}, [
            h('div.strip-name', {}, 'Master'),
            h('div.fader-row', {}, [
                h('div.meter', {}, [meterFill]),
                volEl,
            ]),
            volReadout,
        ]);
    }

    function channelStrip(ch) {
        const volEl = h('input.fader', {
            type: 'range', min: -40, max: 6, step: 0.5,
            value: ch.getVolume(),
            orient: 'vertical',
            onInput: (e) => { ch.setVolume(parseFloat(e.target.value)); volReadout.textContent = fmtDb(ch.getVolume()); },
        });
        const volReadout = h('span.readout', {}, fmtDb(ch.getVolume()));

        const panEl = h('input.pan', {
            type: 'range', min: -1, max: 1, step: 0.05, value: ch.getPan(),
            onInput: (e) => { ch.setPan(parseFloat(e.target.value)); panReadout.textContent = fmtPan(ch.getPan()); },
        });
        const panReadout = h('span.readout.small', {}, fmtPan(ch.getPan()));

        const muteBtn = h('button.btn.mini' + (ch.isMuted() ? '.on' : ''), {
            onClick: () => { ch.setMuted(!ch.isMuted()); muteBtn.classList.toggle('on', ch.isMuted()); },
        }, 'M');
        const soloBtn = h('button.btn.mini.solo' + (ch.isSolo() ? '.on' : ''), {
            onClick: () => { ch.setSolo(!ch.isSolo()); soloBtn.classList.toggle('on', ch.isSolo()); },
        }, 'S');

        // Sends: one row per FX bus. -40 dB on the slider == "off" (treated
        // as -Infinity going into the send level).
        const sends = ch.isFx ? null : sendsBlock(ch);
        const eq = eqBlock(ch);
        const inserts = insertsBlock(ch);
        const meterFill = h('div.meter-fill');
        meterRefs.set(ch.id, meterFill);

        const nameLabel = h('div.strip-name', {
            style: { borderTopColor: ch.color || '#888' },
            title: 'Double-click to rename',
            ondblclick: () => {
                const next = prompt('Rename channel:', ch.name);
                if (!next || next === ch.name) return;
                ch.setName(next);
                ctx.lanes.setName(ch.id, next);
            },
        }, ch.name);

        const colorPicker = h('input', {
            type: 'color', value: ch.color || '#888888', style: { display: 'none' },
            onChange: (e) => {
                const c = e.target.value;
                ch.setColor(c);
                ctx.lanes.setColor(ch.id, c);
            },
        });
        const colorSwatch = h('button.color-swatch', {
            style: { background: ch.color || '#888' },
            title: 'Click to recolor',
            onClick: () => colorPicker.click(),
        });

        return h('div.strip', { dataset: { channel: ch.id } }, [
            nameLabel,
            h('div.strip-meta', {}, [colorSwatch, colorPicker]),
            h('div.strip-toggles', {}, [muteBtn, soloBtn]),
            eq,
            inserts,
            h('div.strip-pan', {}, ['Pan', panEl, panReadout]),
            h('div.fader-row', {}, [
                h('div.meter', {}, [meterFill]),
                volEl,
            ]),
            volReadout,
            sends,
        ]);
    }

    function insertsBlock(ch) {
        const list = h('div.insert-list');
        for (const ins of ch.listInserts()) list.appendChild(insertCard(ch, ins));

        const addSel = h('select.insert-add', {
            onChange: (e) => {
                const libId = e.target.value;
                if (!libId) return;
                ch.addInsert(libId);
                e.target.value = '';
            },
        }, [
            h('option', { value: '' }, '+ FX'),
            ...FX_LIBRARY.map(fx => h('option', { value: fx.id }, fx.name)),
        ]);

        return h('div.strip-inserts', {}, [
            h('div.inserts-header', {}, 'Inserts'),
            list,
            addSel,
        ]);
    }

    function insertCard(ch, ins) {
        const lib = FX_LIBRARY.find(f => f.id === ins.libId);
        const headerColor = lib?.color || '#888';
        const card = h('div.insert-card', { style: { borderLeftColor: headerColor } }, [
            h('div.insert-head', {}, [
                h('span.insert-title', {}, lib?.name || ins.libId),
                h('button.insert-remove', {
                    onClick: () => ch.removeInsert(ins.id),
                    title: 'Remove insert',
                }, '×'),
            ]),
            ...ins.instance.params.map(p => paramControl(p)),
        ]);
        return card;
    }

    function paramControl(p) {
        if (p.type === 'select') {
            return h('label.insert-param', {}, [
                h('span.insert-param-label', {}, p.label),
                h('select.insert-param-input', {
                    onChange: (e) => p.set(e.target.value),
                }, p.options.map(o => {
                    const value = typeof o === 'string' ? o : o.value;
                    const label = typeof o === 'string' ? o : o.label;
                    return h('option', { value, selected: p.get() === value }, label);
                })),
            ]);
        }
        const min = p.min ?? 0;
        const max = p.max ?? 1;
        const step = p.step ?? 0.01;
        const cur = p.get();
        // Log-scale params (e.g. filter freq) feel right when the slider
        // moves linearly through a logarithmic range. We map slider 0–1 →
        // log-interpolated value.
        const isLog = p.scale === 'log';
        const toSlider = isLog ? v => Math.log(v / min) / Math.log(max / min) : v => v;
        const fromSlider = isLog ? s => min * Math.pow(max / min, s) : s => s;
        const sliderMin = isLog ? 0 : min;
        const sliderMax = isLog ? 1 : max;
        const sliderStep = isLog ? 0.001 : step;
        const readout = h('span.insert-param-readout', {}, fmtValue(cur, p));
        const slider = h('input.insert-param-input', {
            type: 'range', min: sliderMin, max: sliderMax, step: sliderStep,
            value: toSlider(cur),
            onInput: (e) => {
                const v = fromSlider(parseFloat(e.target.value));
                p.set(v);
                readout.textContent = fmtValue(v, p);
            },
        });
        return h('label.insert-param', {}, [
            h('span.insert-param-label', {}, p.label),
            slider,
            readout,
        ]);
    }

    function fmtValue(v, p) {
        if (typeof v !== 'number') return String(v);
        const u = p.unit ? ` ${p.unit}` : '';
        if (p.scale === 'log' || (p.max ?? 1) > 100) return Math.round(v) + u;
        return v.toFixed(2) + u;
    }

    function eqBlock(ch) {
        const make = (label, getter, setter) => {
            const slider = h('input.eq-slider', {
                type: 'range', min: -18, max: 18, step: 0.5, value: getter(),
                title: `${label} ${fmtDb(getter())}`,
                onInput: (e) => {
                    const v = parseFloat(e.target.value);
                    setter(v);
                    slider.title = `${label} ${fmtDb(v)}`;
                },
                ondblclick: () => { setter(0); slider.value = 0; slider.title = `${label} 0.0 dB`; },
            });
            return h('div.eq-band', {}, [
                h('span.eq-label', {}, label),
                slider,
            ]);
        };
        return h('div.strip-eq', {}, [
            h('div.eq-header', {}, 'EQ'),
            make('Hi', () => ch.getEqHigh(), (v) => ch.setEqHigh(v)),
            make('Mid', () => ch.getEqMid(), (v) => ch.setEqMid(v)),
            make('Lo', () => ch.getEqLow(), (v) => ch.setEqLow(v)),
        ]);
    }

    function sendsBlock(ch) {
        const fxList = ctx.mixer.fx();
        if (!fxList.length) return null;
        const rows = fxList.map(fx => {
            const cur = ch.getSendLevel(fx.id);
            const initial = isFinite(cur) ? cur : -40;
            const slider = h('input.send-slider', {
                type: 'range', min: -40, max: 0, step: 0.5, value: initial,
                onInput: (e) => {
                    const v = parseFloat(e.target.value);
                    // Treat the bottom of the range as "off".
                    ch.addSend(fx.id, v <= -40 ? -Infinity : v);
                    readout.textContent = v <= -40 ? 'off' : fmtDb(v);
                },
            });
            const readout = h('span.readout.small', {}, isFinite(cur) ? fmtDb(cur) : 'off');
            return h('div.send-row', {}, [
                h('span.send-label', { style: { color: fx.color } }, shortFxName(fx.name)),
                slider,
                readout,
            ]);
        });
        return h('div.strip-sends', {}, [
            h('div.sends-header', {}, 'Sends'),
            ...rows,
        ]);
    }

    function shortFxName(name) {
        const map = { 'Reverb': 'Rev', 'Delay': 'Dly' };
        return map[name] || name.slice(0, 4);
    }

    function fmtDb(v) { return `${v >= 0 ? '+' : ''}${v.toFixed(1)} dB`; }
    function fmtPan(v) {
        if (Math.abs(v) < 0.02) return 'C';
        return v < 0 ? `L${Math.round(-v * 100)}` : `R${Math.round(v * 100)}`;
    }

    render();
    ctx.bus.on('mixer:added', render);
    ctx.bus.on('project:loaded', render);
    // Volume / pan / mute / solo changes update the channel state directly
    // (the slider keeps its DOM value), but inserts add or remove DOM cards,
    // so a full re-render is needed for that field only.
    ctx.bus.on('mixer:changed', (e) => {
        if (!e) return;
        if (e.field === 'inserts' || e.field === 'name' || e.field === 'color') render();
    });
}
